import { createEffect, createSignal, JSX, Show } from 'solid-js'
import { chatStore, settingStore, userStore } from '/web/store'
import { elapsedSince, getAssetUrl } from '/web/shared/util'
import ImageFill from '/web/shared/aifans/ImageFill'
import { A, useNavigate } from '@solidjs/router'
import Fuse from 'fuse.js'
import { RotateCcw, Trash } from 'lucide-solid'
import { getOrCreateChatWithCharacter } from '/web/shared/aifans/lib/chat-util'

const RecentChatsSearch = (props: any) => {
  return (
    <div class="relative mt-3 font-clash">
      <form
        action="/search"
        method="get"
        id="search-form"
        onSubmit={(e) => {
          e.preventDefault()
        }}
      >
        <input
          type="text"
          name="query"
          class="h-[42px] w-full rounded-md border border-cosplay-gray-100 bg-[#fafafa]/10 px-6 focus:outline-none"
          placeholder="Search..."
          onInput={props.onChange}
        />
      </form>
    </div>
  )
}

const RecentChatItem = (props: {
  avatar?: string
  title?: string
  subtitle?: string
  description?: string
  selected?: boolean
  id?: string
}) => {
  return (
    <div>
      <A href={`/chat/${props.id}`} onClick={() => settingStore.leftPanelMobile(false)}>
        <ChatListItem {...props} />
      </A>
    </div>
  )
}

const NewChatItem = (props: {
  avatar?: string
  title?: string
  subtitle?: string
  description?: string
  selected?: boolean
  id?: string
  onClick: (characterId: string) => void
}) => {
  return (
    <div class="cursor-pointer" onClick={() => props.id && props.onClick(props.id)}>
      <ChatListItem {...props} />
    </div>
  )
}

const ChatListItem = (props: {
  avatar?: string
  title?: string
  subtitle?: string
  description?: string
  selected?: boolean
}) => {
  return (
    <div
      class={`flex flex-row items-center rounded-md border ${
        props.selected ? 'border-cosplay-gray-100' : 'border-transparent'
      }`}
    >
      {props.avatar && (
        <ImageFill
          style="h-[64px] w-[54px] flex-shrink-0 rounded-bl-md rounded-tl-md"
          src={getAssetUrl(props.avatar)}
        />
      )}

      <div class="ml-4 flex flex-1 flex-col">
        <h2 class="font-clash text-base font-semibold leading-5 text-white">
          {props.title}{' '}
          {props.subtitle && <span class="text-xs font-normal"> - {props.subtitle}</span>}
        </h2>
        <p class="font-clash text-xs text-cosplay-gray-100">{props.description}</p>
      </div>

      <Show when={props.selected}>
        <div class="flex h-[64px] flex-col justify-end p-2">
          <div class="flex flex-row gap-2">
            <button onClick={() => chatStore.option({ options: false, modal: 'restart' })}>
              <RotateCcw class="h-4 w-4" />
            </button>
            <button onClick={() => chatStore.option({ options: false, modal: 'delete' })}>
              <Trash class="h-4 w-4" />
            </button>
          </div>
        </div>
      </Show>
    </div>
  )
}

const ChatRecentChatsNav = (props: any) => {
  let fuse: any
  const [searchResults, setSearchResults] = createSignal<any[]>([])

  const nav = useNavigate()

  const chats = chatStore((s) => ({
    ready: s.allChars.list.length > 0 && (s.active?.char?._id || 'no-id') in s.allChars.map,
    allChars: s.allChars?.list.filter((char) => {
      const chatExists = s.allChats?.find((chat) => char._id === chat.characterId)
      return !chatExists
    }),
    allChats: s.allChats,
    lastChatId: s.lastChatId,
    recentChats: s.allChats.map((chat) => {
      const character = s.allChars?.list?.find((char) => char._id === chat.characterId)

      return {
        ...chat,
        character: { ...character },
      }
    }),
    favoriteCharacters: s.allChars?.list
      .filter((char) => {
        return userStore().profile?.likedCharacters?.includes(char._id)
      })
      .sort((a, b) => a.name.localeCompare(b.name)),
  }))

  createEffect(() => {
    if (chats?.recentChats) {
      fuse = new Fuse(chats.recentChats, {
        keys: ['character.name', 'character.realName'],
        includeMatches: true,
        includeScore: true,
        minMatchCharLength: 2,
      })

      setSearchResults(chats.recentChats)
    }
  })

  const onChange: JSX.EventHandler<HTMLFormElement, InputEvent> = (e) => {
    const results = fuse.search(e.currentTarget.value)

    if (results.length) {
      setSearchResults(results.map((i: any) => i.item))
    } else {
      setSearchResults(chats?.recentChats)
    }
  }

  const createChat = (characterId: string, presetId?: string) => {
    getOrCreateChatWithCharacter(characterId, (url) => {
      nav(url)
      settingStore.leftPanelMobile(false)
    })
  }

  return (
    <div class="md:w-[310px]">
      <Show when={chats?.recentChats.length > 0}>
        <h2 class="font-clash text-[18px] font-semibold text-cosplay-gray-100">Recent Chats</h2>
        <h3 class="font-clash text-[11px] text-cosplay-gray-100">With your girlfriends</h3>
        <RecentChatsSearch onChange={onChange} />
        <div class="grid grid-cols-1 gap-4 py-7">
          {searchResults()?.map((chat: any) => {
            return (
              <RecentChatItem
                title={chat.character?.name}
                subtitle={chat.character?.realName}
                description={`${elapsedSince(chat.updatedAt)} ago`}
                avatar={chat.character?.avatar}
                selected={chats.lastChatId === chat._id}
                id={chat._id}
              />
            )
          })}
        </div>
      </Show>
      <Show when={chats?.favoriteCharacters.length > 0}>
        <h2 class="font-clash text-[18px] font-semibold text-cosplay-gray-100">Your Favorites</h2>
        <h3 class="font-clash text-[11px] text-cosplay-gray-100">Find your favorites quickly</h3>
        <div class="grid grid-cols-1 gap-4 py-7">
          {chats?.favoriteCharacters?.map((character: any) => {
            // check if a chat exists
            const chat = chats?.allChats?.find((chat) => character._id === chat.characterId)
            if (chat) {
              return (
                <RecentChatItem
                  title={character?.name}
                  subtitle={character?.realName}
                  description={`${elapsedSince(chat.updatedAt)} ago`}
                  avatar={character?.avatar}
                  selected={chats.lastChatId === chat._id}
                  id={chat._id}
                />
              )
            } else {
              return (
                <NewChatItem
                  title={character?.name}
                  subtitle={character?.realName}
                  description={`Start a new chat`}
                  avatar={character?.avatar}
                  selected={chats.lastChatId === character?._id}
                  id={character?._id}
                  onClick={(characterId) => createChat(characterId)}
                />
              )
            }
          })}
        </div>
      </Show>
      <Show when={chats?.allChars.length > 0}>
        <h2 class="font-clash text-[18px] font-semibold text-cosplay-gray-100">Start a new chat</h2>
        <h3 class="font-clash text-[11px] text-cosplay-gray-100">Find your girlfriends</h3>
        <div class="grid grid-cols-1 gap-4 pt-3">
          {chats?.allChars?.map((character: any) => {
            return (
              <NewChatItem
                title={character?.name}
                subtitle={character?.realName}
                description={`Start a new chat`}
                avatar={character?.avatar}
                selected={chats.lastChatId === character?._id}
                id={character?._id}
                onClick={(characterId) => createChat(characterId)}
              />
            )
          })}
        </div>
      </Show>
    </div>
  )
}

export default ChatRecentChatsNav
